import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { CommonModule, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import {
  MatPaginator,
  MatPaginatorModule,
  PageEvent,
} from '@angular/material/paginator';
import { MatRadioButton } from '@angular/material/radio';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { DomSanitizer } from '@angular/platform-browser';
import { Observable, Subscription } from 'rxjs';

import { TableColumnType } from 'src/app/models/table/TableColumnType';

import { ComponentHostDirective } from './component-host.directive';
import { TableFilterComponent } from './table-filter/table-filter.component';
import { BaseQueryParams } from '../../../models/BaseQueryParams';
import { EventTypes } from '../../../models/Enums/EventTypes';
import { StineEvent } from '../../../models/Enums/StineEvent';
import { TableActionButton } from '../../../models/table/TableActionButton';
import { TableColumn } from '../../../models/table/TableColumn';
import { TableColumnCheckBox } from '../../../models/table/TableColumnCheckBox';
import { TableColumnIcon } from '../../../models/table/TableColumnIcon';
import { TableColumnRadio } from '../../../models/table/TableColumnRadio';
import { TableExpandColumn } from '../../../models/table/TableExpandColumn';
import { TableOptions } from '../../../models/table/TableOptions';
import { NumberFieldDirective } from '../../directives/number-field.directive';

@Component({
  standalone: true,
  selector: 'stine-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  imports: [
    MatTableModule,
    MatSortModule,
    MatIconModule,
    NgTemplateOutlet,
    MatPaginatorModule,
    MatButtonModule,
    ComponentHostDirective,
    CommonModule,
    MatDividerModule,
    MatRadioButton,
    MatCheckboxModule,
    MatTooltip,
    MatInputModule,
    FormsModule,
    NumberFieldDirective,
  ],
  animations: [
    trigger('expand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition(
        'expanded <=> collapsed',
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'),
      ),
    ]),
  ],
})
export class TableComponent implements OnInit, OnDestroy {
  @ContentChild(TemplateRef)
  public expandedTemplateRef: TemplateRef<any>;
  public totalRowData: any;
  private _totalRecords?: number;
  @ViewChild(MatPaginator) public matPaginator: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @Input() public resetPageSizeOnTotalChange: boolean;
  @Input() public tableName: string;
  @Input() public tableNameOptions: string;
  /**
   * Generic observable of an array that represents the row data to be displayed in the table.
   * This data will be async piped by the component and subscription managed automatically.
   * @type {Observable<any[]>}
   */
  @Input() public data: Observable<any[]>;
  /**
   * Column definitions for the table. Should contain one for each column that contains data from the provided dataset.
   * @type {TableColumn[]}
   */
  @Input() public columns: any[];
  /**
   * Defines a set of available options for configuring how the table behaves.
   * See the TableOptions class for details on each option.
   * @type {TableOptions}
   */
  @Input() public options: TableOptions;
  /**
   * Defines the column properties of the additional expanded row for each row in the table.
   * @type {TableExpandColumn}
   */
  @Input() public expandColumn?: TableExpandColumn;
  /**
   * Defines actions that can be taken on a row, each one will create a button using the configuration
   * provided and execute the callback function with an event and the data row as the parameter.
   * @type {TableActionButton[]}
   */
  @Input() public actionButtons?: TableActionButton[];
  /**
   * Set of parameters provided by consumers to tell the table component what to set as initial values.
   * @type {BaseQueryParams}
   */
  @Input() public queryParameters?: BaseQueryParams;

  /**
   * Indicates what the total row count despite page size
   */
  //@Input() public totalRecords?: number;
  @Input()
  public set totalRecords(value: number | undefined) {
    if (this._totalRecords !== value) {
      this._totalRecords = value;
      this.onTotalRecordsChange();
      if (this.resetPageSizeOnTotalChange) {
        this.paginator.pageSize = this.options.pageSizeOptions[0];
      }
    }
  }

  public get totalRecords(): number | undefined {
    return this._totalRecords;
  }

  @Input() rowStyleFn?: (row: any) => { [key: string]: string };
  /**
   * Callback function provided as a pass through to receive events from the filter component.
   * Note: Only used if this event is subscribed to by a consumer
   * @type {EventEmitter<StineEvent<string>>}
   */
  @Output() public filterChange: EventEmitter<StineEvent<string>>;
  /**
   * Callback function to receive various events output by the Table component.
   * See EventTypes enum for possible event types.
   * @type {EventEmitter<StineEvent<any>>}
   */
  @Output() public eventHandler: EventEmitter<StineEvent<any>>;

  public expandedRow: any;
  public tableParameters: BaseQueryParams;
  public columnsToDisplay: string[];
  public pageSize: number;

  private subscriptions: Subscription[];
  @ViewChild(ComponentHostDirective, { static: true })
  private filterHost!: ComponentHostDirective;
  @ViewChild(MatPaginator)
  private paginator: MatPaginator;
  public renderTable: boolean = false;

  constructor(private sanitizer: DomSanitizer) {
    this.subscriptions = [];
    this.filterChange = new EventEmitter<StineEvent<string>>();
    this.eventHandler = new EventEmitter<StineEvent<any>>();
    this.options = new TableOptions();
    this.columns = [];
    this.columnsToDisplay = [];
    this.tableParameters = new BaseQueryParams();
    this.totalRowData = {};
    this.resetPageSizeOnTotalChange = false;
  }

  ngOnInit(): void {
    this.columnsToDisplay = this.columns.map((x) => x.definition);
    this.tableParameters.pageSize = this.options?.pageSizeOptions[0];
    this.checkFilterUsage();
    this.checkActionUsage();

    if (this.paginator) {
      this.initializePaginator();
    }

    if (this.queryParameters) {
      this.tableParameters.updateByObject(this.queryParameters);
    }

    if (this.options.showTotalsRow) {
      this.calculateTotals();
    } else {
      this.setRenderTable();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Handles the FilterChange event from the TableFilterComponent. It will reset the paginator
   * and pass the filter event along to the consumer.
   * @param {StineEvent<string>} event
   */
  public onFilterChange(event: StineEvent<string>): void {
    this.tableParameters.filter = event.data;
    if (this.paginator) {
      this.tableParameters.pageIndex = 0;
      this.tableParameters.pageSize = this.paginator.pageSize;
      this.paginator.pageIndex = 0;
    }
    this.filterChange.emit(event);
  }

  /**
   * Handles MatSortChange events internally. Sets the active and direction properties of Sort
   * to the equivalent properties of a BaseQueryParams object.
   * Note: If a paginator is used, it will reset the index to 0.
   * @param {Sort} event
   */
  public onSortChange(event: Sort): void {
    // 1. Check if the direction is being cleared (third click)
    if (event.direction === '') {
      // 1. Force the direction back to 'asc'
      event.direction = 'asc';

      // 2. ***Crucial*** Explicitly set the sort state on the MatSort directive
      // This forces the header icon to re-render correctly.
      this.sort.sort({
        id: event.active,
        start: 'asc',
      } as any);

      // Since we're explicitly setting the sort, we might need to stop
      // the original event from propagating.
      return;
    }

    this.tableParameters.sort = event.direction ? event.active : '';
    this.tableParameters.descending = event.direction === 'desc';

    if (this.paginator) {
      this.tableParameters.pageIndex = 0;
      this.paginator.pageIndex = 0;
    }

    this.eventHandler.emit(
      new StineEvent(EventTypes.Table, this.tableParameters),
    );
  }

  /**
   * Handles the user clicking on a row.
   * It only responds to expanding expandable rows. Use TableActions for actions to perform by row.
   * @param row
   * @param $event
   */
  public rowClicked(row: any, $event: any): void {
    if (this.options?.alwaysExpanded === false) {
      this.expandedRow = this.expandedRow === row ? null : row;
      $event.stopPropagation();
    }
  }

  /**
   * Used to determine how many columns the expanded column should span if a
   * column span is not provided by the expandColumn.colSpan property.
   * Default behavior is to span all columns and action column, if present.
   * @returns {number}
   */
  public calculateColSpan(): number {
    const columns = this.columns?.length ?? 0;
    const actions =
      this.actionButtons && this.actionButtons?.length > 0 ? 1 : 0;
    return columns + actions;
  }

  /**
   * Used to reset the page to first page when the totalRecords are changed which we get via
   * @Input() TotalRecords.
   */

  private onTotalRecordsChange(): void {
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
  }

  /**
   * Uses the expandColumn.colSkip property to determine if the table should push expanded rows
   * further out by colSkip number of columns from the left. It does this by adding a column
   * to display the 'spacer' column.
   * @returns {string[]}
   */
  public calcExpandColumnSkip(): string[] {
    if (this.expandColumn?.colSkip || 0 > 0) {
      return ['expandSpacer', 'expandedRowColumn'];
    }
    return ['expandedRowColumn'];
  }

  /**
   * Handles PageEvents from MatPaginator
   * @param $event
   */
  public onPageChange($event: PageEvent): void {
    this.tableParameters.pageIndex = $event.pageIndex;
    this.tableParameters.pageSize = $event.pageSize;
    this.eventHandler.emit(
      new StineEvent(EventTypes.Table, this.tableParameters),
    );
  }

  private checkFilterUsage(): void {
    if (this.filterChange.observed) {
      const filterComponentRef =
        this.filterHost.viewContainerRef.createComponent<TableFilterComponent>(
          TableFilterComponent,
        );
      this.subscriptions.push(
        filterComponentRef.instance.filterChange.subscribe((filterValue) => {
          this.onFilterChange(filterValue);
        }),
      );
    }
  }

  private checkActionUsage(): void {
    if (this.actionButtons && this.actionButtons?.length > 0) {
      this.columnsToDisplay.push('actions');
    }
  }

  private initializePaginator(): void {
    this.paginator.pageSizeOptions = this.options.pageSizeOptions;
    this.paginator.pageSize = this.options.pageSizeOptions[0];
    this.paginator.length = 0;
    this.paginator.pageIndex = 0;
  }

  public setRenderTable(): void {
    this.subscriptions.push(
      this.data?.subscribe((rows) => {
        this.renderTable = rows?.length > 0;
      }),
    );
  }

  /**
   * Used to calculate total row for columns that are mark as true in
   * TableColumn.options.showTotal field.
   * @returns {void}
   */
  private calculateTotals(): void {
    // Loop through the rows in data and calculate totals for the columns that need to be totaled.
    this.subscriptions.push(
      this.data?.subscribe((rows) => {
        // Reset the total row data.
        this.totalRowData = {};
        rows?.forEach((row) => {
          this.columns.forEach((col) => {
            if (col.options?.showTotal) {
              // Initialize the total for the column if it doesn't exist.
              if (!this.totalRowData[col.definition]) {
                this.totalRowData[col.definition] = 0;
              }
              // Add the column value to the total.
              this.totalRowData[col.definition] += Number(row[col.definition]);
            }
          });
        });
        this.renderTable =
          this.totalRowData && Object.keys(this.totalRowData).length != 0;
      }),
    );
  }

  public columnIsRadioType(column: TableColumn): boolean {
    return column instanceof TableColumnRadio;
  }

  public columnIsCheckBoxType(column: TableColumn): boolean {
    return column instanceof TableColumnCheckBox;
  }

  public columnIsIconType(column: TableColumn): boolean {
    return column instanceof TableColumnIcon;
  }

  public columnIsInputType(column: TableColumn): boolean {
    return column.options.columnType === TableColumnType.InputNumber;
  }

  public checkForLink(column: any, row: unknown): boolean {
    if (column.link && column.link(row)) {
      return true;
    }
    return false;
  }

  public sanitizeInnerHtml(column: TableColumn, row: any): any {
    return this.sanitizer.bypassSecurityTrustHtml(`${column.display?.(row)}`);
  }

  public checkForInnerHtml(column: TableColumn): boolean {
    if (column.options?.isHtml) {
      return true;
    }
    return false;
  }

  public handleLinkClick(event: MouseEvent, link: string): void {
    window.open(link, '_blank');
  }

  /**
   * Check if the column has clickable content
   */
  public checkForClickableContent(column: TableColumn, row: any): boolean {
    if (!column.options?.isHtml) return false;

    const content = column.display?.(row) || '';
    return `${content}`.includes('(click)') || `${content}`.includes('cursor-pointer');
  }

  /**
 * Handle column click events
 */
public handleColumnClick(column: TableColumn, row: any): void {
  // Check if column has a custom click handler
  if (column.valueClickHandler) {
    column.valueClickHandler(row);
  }
}
}
